import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { useGetDepartedVehiclesQuery } from '../../api/compoundManagementApi';
import { handleQueryError } from '../../notifications/ErrorNotification';
import {
    getCompoundManagementModalFilter,
    getDebouncedVinSearchFilter,
    getDepartedVehiclesSorting,
    mapDepartedVehiclesFilterQueryParams,
} from '../../reducers/compoundManagementSlice';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { CompoundManagementToolbar } from '../shared/toolbar/CompoundManagementToolbar';
import { DepartedVehiclesTable } from './table/DepartedVehiclesTable';
import { departedVehiclesColumnDefinition } from './table/DepartedVehiclesTableColumn';

export const DEFAULT_COMPOUND_DEPARTED_VEHICLES_LIMIT_SIZE = 100;
export const MAX_COMPOUND_DEPARTED_VEHICLES_LIMIT_SIZE = 1000;
const POLLING_INTERVAL = 60000;

export const DepartedVehicles: FunctionComponent = () => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const filters = useAppSelector(getCompoundManagementModalFilter);
    const debouncedVinSearch = useAppSelector(getDebouncedVinSearchFilter);
    const sorting = useAppSelector(getDepartedVehiclesSorting);

    const [limit, setLimit] = useState(DEFAULT_COMPOUND_DEPARTED_VEHICLES_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_COMPOUND_DEPARTED_VEHICLES_LIMIT_SIZE;

    const {
        data: departedVehiclesPage,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetDepartedVehiclesQuery(
        {
            ...mapDepartedVehiclesFilterQueryParams(filters, debouncedVinSearch),
            limit,
            sortBy: sorting,
        },
        {
            pollingInterval: isSessionExpired ? undefined : POLLING_INTERVAL,
        },
    );

    useSetUserTriggeredFetching({
        isFetching,
        depsWhichTriggerUserFetching: [sorting, filters, debouncedVinSearch],
    });

    if (isError) {
        console.error(error);
        handleQueryError(error);
    }

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (departedVehiclesPage === undefined) {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const departedVehicles = departedVehiclesPage.items;
    return (
        <TableColumnSettingsState tableId="compound.departed" columnDefinition={departedVehiclesColumnDefinition}>
            <CompoundManagementToolbar />
            <DepartedVehiclesTable vehicles={departedVehicles} />
            {departedVehicles.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={departedVehiclesPage.hasMore}
                    limitReached={nextLimit > MAX_COMPOUND_DEPARTED_VEHICLES_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};
